import React, { useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import { Link, useSearchParams } from 'react-router-dom';
import API from '../api';
import './DashtremeTheme.css';

interface MonthlyTrend {
  month: string;
  entries: number;
  completed: number;
  amount: number;
}

interface RecentEntry {
  id: number;
  bill_no: string;
  total_amount: number;
  entry_date: string;
  test_status: string | number;
  bill_status: string;
  test_name: string;
  P_name: string;
  P_code: string;
}

interface GrowthData {
  patientGrowth: string;
  collectionGrowth: string;
  visitsGrowth: string;
  pendingGrowth: string;
}

interface ProgressData {
  patientProgress: number;
  collectionProgress: number;
  visitsProgress: number;
  pendingProgress: number;
}

interface DashboardData {
  fromDate?: string;
  toDate?: string;
  patientCount?: number;
  todayVisit?: number;
  todayPending?: number;
  completedReport?: number;
  todayCollection?: number;
  monthlyCollection?: number;
  totalCollection?: number;
  totalCompleted?: number;
  totalPending?: number;
  monthlyTrends?: MonthlyTrend[];
  recentEntries?: RecentEntry[];
  growthData?: GrowthData;
  progressData?: ProgressData;
}

const fmtNum = (val?: number) => (val != null ? val.toLocaleString() : '0');
const fmtCurr = (val?: number) => (val != null ? `₹${val.toLocaleString()}` : '₹0');

const dashboardToday = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

const Dashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const appliedFromDate = searchParams.get('from_date') || dashboardToday();
  const appliedToDate = searchParams.get('to_date') || dashboardToday();
  const [fromDate, setFromDate] = useState(appliedFromDate);
  const [toDate, setToDate] = useState(appliedToDate);
  const [refreshKey, setRefreshKey] = useState(0);

  // Restore the submitted dates on reload and browser Back/Forward.
  useEffect(() => {
    setFromDate(appliedFromDate);
    setToDate(appliedToDate);
  }, [appliedFromDate, appliedToDate]);
  const [data, setData] = useState<DashboardData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'entries' | 'amount'>('entries');
  const [chartKey, setChartKey] = useState(0); // force chart re-mount when data arrives
  const areaChartRef = useRef<HTMLDivElement>(null);
  const donutChartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setData((previous) => ({ patientCount: previous.patientCount }));
    const fetchData = async () => {
      try {
        const res = await API.get('/dashboard/summary', { params: { from_date: appliedFromDate, to_date: appliedToDate }, signal: controller.signal });
        if (res.data?.success && res.data?.data) {
          if (controller.signal.aborted) return;
          if (res.data.data.fromDate !== appliedFromDate || res.data.data.toDate !== appliedToDate) {
            setError('Unable to load data for the selected date range. Please try again.');
            return;
          }
          setData(res.data.data);
          setChartKey((prev) => prev + 1); // trigger chart re-mount with real data
        } else {
          setError('Failed to fetch dashboard data');
        }
      } catch (err: any) {
        if (controller.signal.aborted) return;
        console.error('Dashboard error:', err);
        setError(err?.response?.data?.message || err?.message || 'Failed to load dashboard data');
        setChartKey((prev) => prev + 1); // still re-mount so fallback data renders
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchData();
    return () => controller.abort();
  }, [appliedFromDate, appliedToDate, refreshKey]);

  const handleFilterSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) return;
    if (!fromDate || !toDate || fromDate > toDate) {
      setError('From Date must be on or before To Date.');
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('from_date', fromDate);
    nextParams.set('to_date', toDate);
    if (fromDate === appliedFromDate && toDate === appliedToDate) {
      setSearchParams(nextParams);
      setRefreshKey((previous) => previous + 1);
    } else {
      setSearchParams(nextParams);
    }
  };

  // ── Force resize dispatch after data loads so ApexCharts redraws ────────
  useEffect(() => {
    if (!loading) {
      const t1 = setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
      const t2 = setTimeout(() => window.dispatchEvent(new Event('resize')), 400);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [loading, chartKey]);

  // ── Sidebar toggle observer ───────────────────────────────────────────────
  useEffect(() => {
    let timer: any;
    const handleBodyMutation = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 310);
    };

    const observer = new MutationObserver(handleBodyMutation);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  // ── ResizeObserver: trigger chart redraw when container size changes ──────
  useEffect(() => {
    const nodes = [areaChartRef.current, donutChartRef.current].filter(Boolean) as HTMLDivElement[];
    if (!nodes.length) return;
    const ro = new ResizeObserver(() => {
      window.dispatchEvent(new Event('resize'));
    });
    nodes.forEach((n) => ro.observe(n));
    return () => ro.disconnect();
  }, [loading]);

  // ── Stat cards ──────────────────────────
  const statCards = useMemo(
    () => [
      { label: 'Total Patients', value: fmtNum(data.patientCount), icon: 'fa-users', totalPatients: true, href: '/patients', navigationLabel: 'Open Patient List' },
      {
        label: 'Collection',
        value: fmtCurr(data.monthlyCollection),
        icon: 'fa-wallet',
        href: '/billing',
        navigationLabel: 'Open Billing',
      },
      {
        label: 'Visits',
        value: fmtNum(data.todayVisit),
        icon: 'fa-user-clock',
        href: '/patients-reports?tab=completed',
        navigationLabel: 'Open Receipts Completed tab',
      },
      {
        label: 'Pending',
        value: fmtNum(data.todayPending),
        icon: 'fa-clock',
        href: '/patients-reports?tab=pending',
        navigationLabel: 'Open Receipts Pending Tests tab',
      },
    ],
    [data]
  );

  // ── Chart 1: Area Chart Options & Series — always use real DB data ────────
  const monthLabels = useMemo(
    () => (data.monthlyTrends || []).map((t) => t.month),
    [data.monthlyTrends]
  );

  const entriesSeriesData = useMemo(
    () => (data.monthlyTrends || []).map((t) => t.entries),
    [data.monthlyTrends]
  );

  const completedSeriesData = useMemo(
    () => (data.monthlyTrends || []).map((t) => t.completed),
    [data.monthlyTrends]
  );

  const amountSeriesData = useMemo(
    () => (data.monthlyTrends || []).map((t) => t.amount),
    [data.monthlyTrends]
  );

  const areaChartOptions = useMemo<any>(
    () => ({
      chart: {
        type: 'area',
        height: 320,
        toolbar: { show: false },
        background: 'transparent',
        fontFamily: 'Inter, sans-serif',
        sparkline: { enabled: false },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 350,
        },
      },
      theme: { mode: 'dark' },
      colors: ['#00e396', '#ffffff'],
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.55,
          opacityTo: 0.08,
          stops: [0, 90, 100],
        },
      },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 2.5 },
      grid: {
        borderColor: 'rgba(255, 255, 255, 0.1)',
        strokeDashArray: 3,
        xaxis: { lines: { show: false } },
      },
      xaxis: {
        categories: monthLabels,
        labels: { style: { colors: 'rgba(255,255,255,0.7)', fontSize: '11px' } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: { colors: 'rgba(255,255,255,0.7)', fontSize: '11px' },
          formatter: (val: number) => Math.round(val),
        },
      },
      tooltip: {
        theme: 'dark',
        style: { fontSize: '12px' },
      },
    }),
    [monthLabels]
  );

  const areaChartSeries = useMemo(() => {
    if (activeTab === 'entries') {
      return [
        { name: 'Patient Entries', data: entriesSeriesData },
        { name: 'Completed Reports', data: completedSeriesData },
      ];
    }
    return [
      { name: 'Monthly Revenue (₹)', data: amountSeriesData },
    ];
  }, [activeTab, entriesSeriesData, completedSeriesData, amountSeriesData]);

  // ── Chart 2: Donut Chart Options & Series (Memoized) ─────────────────────
  const totalCompleted = data.totalCompleted ?? data.completedReport ?? 0;
  const totalPending = data.totalPending ?? data.todayPending ?? 0;
  const todayVisits = data.todayVisit ?? 0;
  const grandTotalReports = totalCompleted + totalPending + todayVisits;

  const donutChartOptions = useMemo<any>(
    () => ({
      chart: {
        type: 'donut',
        height: 270,
        background: 'transparent',
        animations: {
          enabled: true,
          speed: 350,
        },
      },
      theme: { mode: 'dark' },
      colors: ['#00e396', '#ffab00', '#008ffb'],
      labels: ['Completed Reports', 'Pending Reports', "Today's Visits"],
      stroke: { width: 0 },
      dataLabels: { enabled: false },
      legend: { show: false },
      plotOptions: {
        pie: {
          donut: {
            size: '76%',
            background: 'transparent',
            labels: {
              show: true,
              name: { show: true, color: 'rgba(255,255,255,0.65)', fontSize: '13px' },
              value: {
                show: true,
                color: '#ffffff',
                fontSize: '22px',
                fontWeight: '700',
                formatter: (val: any) => val,
              },
              total: {
                show: true,
                label: 'Total Activity',
                color: 'rgba(255,255,255,0.7)',
                formatter: () => `${grandTotalReports}`,
              },
            },
          },
        },
      },
      tooltip: { theme: 'dark' },
    }),
    [grandTotalReports]
  );

  // Donut uses real totals; guard: if all zero show equal slices so ring is visible
  const donutChartSeries = useMemo(() => {
    const series = [totalCompleted, totalPending, todayVisits];
    return series.some((v) => v > 0) ? series : [1, 1, 1];
  }, [totalCompleted, totalPending, todayVisits]);

  const calcPct = (val: number) => (grandTotalReports > 0 ? ((val / grandTotalReports) * 100).toFixed(1) : '0');

  return (
    <div className="dashtreme-dashboard">
      {error && (
        <div className="alert alert-danger bg-danger text-white border-0 rounded-3 mb-4 d-flex justify-content-between align-items-center">
          <div><i className="fa fa-exclamation-triangle me-2" />{error}</div>
          <button type="button" className="btn-close btn-close-white" onClick={() => setError('')} />
        </div>
      )}

      <div className="dashboard-header mb-3">
        <h1 className="h4 mb-0 text-white fw-bold">Dashboard</h1>
        <form className="dashboard-date-filters" onSubmit={handleFilterSubmit}>
          <input id="dashboard-from-date" type="date" className="form-control" aria-label="From Date"
            value={fromDate} onChange={(event) => setFromDate(event.target.value)} required disabled={loading} />
          <input id="dashboard-to-date" type="date" className="form-control" aria-label="To Date"
            value={toDate} min={fromDate} onChange={(event) => setToDate(event.target.value)} required disabled={loading} />
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Loading…' : 'Submit'}
          </button>
        </form>
      </div>

      {/* ── Top Stat Cards Row ────────────────────────────────────────────── */}
      <div className="row g-3 mb-4">
        {statCards.map((card, idx) => (
          <div key={idx} className="col-12 col-sm-6 col-xl-3">
            <div className="dashtreme-card p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="dashtreme-stat-val mb-1">
                    {card.totalPatients && data.patientCount != null ? card.value : loading ? <span className="spinner-border spinner-border-sm text-light" /> : error ? '—' : card.value}
                  </div>
                </div>
                <div className="dashtreme-icon ms-auto">
                  <i className={`fa ${card.icon}`} />
                </div>
              </div>

              <div className="d-flex align-items-center justify-content-between mt-2">
                <span className="dashtreme-stat-label">{card.label}</span>
                <Link to={card.href} className="dashboard-card-arrow" aria-label={card.navigationLabel} title={card.navigationLabel}>
                  <i className="fa fa-arrow-right" aria-hidden="true" />
                </Link>

              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Middle Row: Site Traffic Area Chart + Donut Chart ─────────────── */}
      <div className="row g-3 mb-4">
        {/* Site Traffic (8 Cols) */}
        <div className="col-12 col-lg-8">
          <div className="dashtreme-card p-4 h-100">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="mb-0 text-white fw-bold">Site Traffic</h5>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className={`dashtreme-pill ${activeTab === 'entries' ? 'active' : ''}`}
                  onClick={() => setActiveTab('entries')}
                >
                  New Visitor
                </button>
                <button
                  type="button"
                  className={`dashtreme-pill ${activeTab === 'amount' ? 'active' : ''}`}
                  onClick={() => setActiveTab('amount')}
                >
                  Revenue Trend
                </button>
                <i className="fa fa-ellipsis-h text-white-50 ms-2" style={{ cursor: 'pointer' }} />
              </div>
            </div>

            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: 320 }}>
                <div className="spinner-border text-light" />
              </div>
            ) : (
              <div ref={areaChartRef} style={{ width: '100%', height: 320 }}>
                <Chart key={`area-${chartKey}`} options={areaChartOptions} series={areaChartSeries} type="area" height={320} width="100%" />
              </div>
            )}
          </div>
        </div>

        {/* Weekly Sales Donut Chart (4 Cols) */}
        <div className="col-12 col-lg-4">
          <div className="dashtreme-card p-4 h-100 d-flex flex-column justify-content-between">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h5 className="mb-0 text-white fw-bold">Weekly Sales</h5>
              <i className="fa fa-cog text-white-50" style={{ cursor: 'pointer' }} />
            </div>

            {loading ? (
              <div className="d-flex justify-content-center align-items-center my-auto" style={{ height: 250 }}>
                <div className="spinner-border text-light" />
              </div>
            ) : (
              <div ref={donutChartRef} className="my-auto position-relative" style={{ width: '100%', height: 270 }}>
                <Chart key={`donut-${chartKey}`} options={donutChartOptions} series={donutChartSeries} type="donut" height={270} width="100%" />
              </div>
            )}

            {/* Legend Breakdown Below Chart */}
            <div className="pt-3 border-top border-white-10">
              <div className="d-flex align-items-center justify-content-between mb-2 fs-7">
                <span className="d-flex align-items-center text-white-75">
                  <span className="rounded-circle me-2" style={{ width: 10, height: 10, background: '#00e396' }} />
                  Completed Reports
                </span>
                <span className="fw-semibold text-white">{totalCompleted} ({calcPct(totalCompleted)}%)</span>
              </div>

              <div className="d-flex align-items-center justify-content-between mb-2 fs-7">
                <span className="d-flex align-items-center text-white-75">
                  <span className="rounded-circle me-2" style={{ width: 10, height: 10, background: '#ffab00' }} />
                  Pending Reports
                </span>
                <span className="fw-semibold text-white">{totalPending} ({calcPct(totalPending)}%)</span>
              </div>

              <div className="d-flex align-items-center justify-content-between fs-7">
                <span className="d-flex align-items-center text-white-75">
                  <span className="rounded-circle me-2" style={{ width: 10, height: 10, background: '#008ffb' }} />
                  Today Visits
                </span>
                <span className="fw-semibold text-white">{todayVisits} ({calcPct(todayVisits)}%)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom Row: Recent Patient Entries Live Table ──────────────────── */}
      <div className="row g-3">
        <div className="col-12">
          <div className="dashtreme-card p-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="mb-0 text-white fw-bold">Recent Patient Entries</h5>
              <span className="badge bg-white bg-opacity-10 text-white px-3 py-2 rounded-pill">
                Live DB Records
              </span>
            </div>

            <div className="table-responsive">
              <table className="table dashtreme-table align-middle">
                <thead>
                  <tr>
                    <th>Bill No</th>
                    <th>Patient Code</th>
                    <th>Patient Name</th>
                    <th>Test Name</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-white-50">
                        <div className="spinner-border spinner-border-sm me-2" /> Loading recent entries...
                      </td>
                    </tr>
                  ) : (data.recentEntries || []).length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-white-50">
                        No patient entries found for the selected date range.
                      </td>
                    </tr>
                  ) : (
                    (data.recentEntries || []).map((row) => (
                      <tr key={row.id}>
                        <td className="fw-bold text-white">#{row.bill_no || row.id}</td>
                        <td>{row.P_code || '—'}</td>
                        <td className="fw-semibold">{row.P_name || 'Walk-in Patient'}</td>
                        <td>{row.test_name || 'General Test'}</td>
                        <td className="text-white-75">{row.entry_date ? String(row.entry_date).split('T')[0] : '—'}</td>
                        <td className="fw-bold text-white">₹{row.total_amount?.toLocaleString() || 0}</td>
                        <td>
                          {String(row.test_status) === '1' ? (
                            <span className="dashtreme-badge-success">Completed</span>
                          ) : (
                            <span className="dashtreme-badge-warning">Pending</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;